import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import ImageUploader from '../components/ImageUploader';

/**
 * E-Books — read free on the platform; the download carries a price once
 * the gateway is live, and printOrderSku links the physical copy in
 * Products. Upload, replace with an updated file, retire — all here.
 */
const EMPTY = {
  title: '', author: '', category: 'Philosophy', description: '',
  cover: '', fileUrl: '', readFree: true, downloadPriceInr: '', printOrderSku: '', sourceUrl: '', published: true,
};
const CATEGORIES = ['Philosophy', 'Para-psychology', 'Health & Yoga', "Children's", 'History', 'Literature & Songs', 'Ecology', 'PROUT', 'Social'];

const EbooksManager = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(EMPTY);

  useEffect(() => { fetchRows(); }, []);

  const fetchRows = async () => {
    try {
      const res = await api.get('/media/ebooks?all=1');
      setRows(res.data.ebooks || []);
    } catch (err) { console.error('Failed to fetch ebooks', err); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditId(null); setFormData(EMPTY); setShowModal(true); };
  const openEdit = (r) => { setEditId(r._id); setFormData({ ...EMPTY, ...r, downloadPriceInr: r.downloadPriceInr ?? '' }); setShowModal(true); };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) { alert('Title is required.'); return; }
    const payload = { ...formData, downloadPriceInr: formData.downloadPriceInr === '' ? null : Number(formData.downloadPriceInr) };
    try {
      if (editId) await api.put(`/media/ebooks/${editId}`, payload);
      else await api.post('/media/ebooks', payload);
      setShowModal(false); fetchRows();
    } catch (err) { alert(err.response?.data?.message || 'Save failed'); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/media/ebooks/${r._id}`); fetchRows(); }
    catch (err) { alert('Delete failed'); }
  };

  const field = 'w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#2A2858]';

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">E-Books</h1>
          <p className="text-sm opacity-70">Read free on the platform; downloads carry a price once payments go live.</p>
        </div>
        <button type="button" onClick={openCreate} className="bg-[#2A2858] text-white font-bold px-5 py-3 rounded-xl hover:bg-[#14122B] transition-all shadow-lg active:scale-95">+ New E-Book</button>
      </div>

      {loading ? <p className="opacity-70">Loading…</p> : rows.length === 0 ? (
        <div className="rounded-xl border border-stone-200 p-8 text-center opacity-80">No e-books yet — upload the first one above.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-stone-200">
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b border-stone-200">
              <th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Download ₹</th><th className="p-3">File</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id} className="border-b border-stone-100 last:border-0">
                  <td className="p-3 font-medium">{r.title}{r.author ? <span className="opacity-60"> — {r.author}</span> : null}</td>
                  <td className="p-3">{r.category}</td>
                  <td className="p-3">{r.downloadPriceInr == null ? '—' : `₹${r.downloadPriceInr}`}</td>
                  <td className="p-3">{r.fileUrl ? <a href={r.fileUrl} target="_blank" rel="noopener noreferrer" className="text-[#2A2858] underline">file</a> : <span className="opacity-50">none</span>}</td>
                  <td className="p-3"><span className={`px-3 py-1 rounded-full text-xs font-bold ${r.published ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>{r.published ? 'Published' : 'Draft'}</span></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button type="button" onClick={() => openEdit(r)} className="px-3 py-1 rounded-lg text-xs font-bold bg-stone-100 hover:bg-stone-200 mr-2">Edit</button>
                    <button type="button" onClick={() => handleDelete(r)} className="px-3 py-1 rounded-lg text-xs font-bold bg-red-50 text-red-600 hover:bg-red-100">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">{editId ? 'Edit E-Book' : 'New E-Book'}</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-4">
              <input name="title" value={formData.title} onChange={handleChange} placeholder="Title *" className={field} />
              <div className="grid grid-cols-2 gap-4">
                <input name="author" value={formData.author} onChange={handleChange} placeholder="Author" className={field} />
                <select name="category" value={formData.category} onChange={handleChange} className={field}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Description" rows={2} className={field} />
              <div className="grid grid-cols-2 gap-4">
                <input type="number" name="downloadPriceInr" value={formData.downloadPriceInr} onChange={handleChange} placeholder="Download price ₹ (blank = not sold)" className={field} />
                <input name="printOrderSku" value={formData.printOrderSku} onChange={handleChange} placeholder="Print-copy product SKU (e.g. amp-001)" className={field} />
              </div>
              <input name="fileUrl" value={formData.fileUrl} onChange={handleChange} placeholder="E-book file URL (uploaded document)" className={field} />
              <div>
                <p className="text-sm mb-2">Cover image</p>
                <ImageUploader onUploadSuccess={(url) => setFormData((f) => ({ ...f, cover: url }))} />
              </div>
              <input name="sourceUrl" value={formData.sourceUrl} onChange={handleChange} placeholder="Source URL (publisher page)" className={field} />
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="readFree" checked={formData.readFree} onChange={handleChange} /> Read free on platform</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="published" checked={formData.published} onChange={handleChange} /> Published</label>
              </div>
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-stone-100 text-stone-500 font-bold py-3.5 rounded-xl hover:bg-stone-200 transition-all">Discard</button>
                <button type="submit" className="flex-1 bg-[#2A2858] text-white font-bold py-3.5 rounded-xl hover:bg-[#14122B] transition-all shadow-lg active:scale-95">{editId ? 'Update E-Book' : 'Create E-Book'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EbooksManager;
